// Streaming file downloads via the service worker (public/download-sw.js).
//
// Goal: "click Download → it lands in my Downloads folder", like any file off
// the internet — no Save-As prompt and no multi-gigabyte Blob pinned in
// memory. The page opens a MessagePort with the worker and points a hidden
// iframe at /__poku-download; the worker answers with a stream fed by that
// port plus Content-Disposition: attachment, so the browser performs a normal
// download it manages itself.
//
// The writer returned here matches the savePicker contract used by the
// downloaders in hls-download.js: { write(buf), close(), abort() }.
//
// Why a port instead of transferring a ReadableStream: postMessage accepts a
// transferred stream without complaint in Chrome, but the worker receives an
// undefined one (verified 2026-09-24 — the response came back 500). Ports are
// supported everywhere and are what a service worker is meant to talk over.

const WORKER_URL = "/download-sw.js";
const DOWNLOAD_PATH = "/__poku-download";
// How long to wait for the worker to acknowledge the handoff. It is already
// running (we awaited serviceWorker.ready), so this only trips when the worker
// did not take the job — better to fall back than to sit at 0%.
const READY_TIMEOUT_MS = 6000;
// A write that never resolves means nothing is consuming the stream — the
// browser refused the download. Fail fast so the caller can fall back instead
// of hanging at 0%.
const PICKUP_TIMEOUT_MS = 15000;

export class StreamDownloadUnavailable extends Error {}

// A cancel, however it arrived, has to look like an AbortError: the
// downloaders surface every other error to the user as a failure.
function abortError() {
  const e = new Error("Download cancelled");
  e.name = "AbortError";
  return e;
}

let readyPromise = null;

export function streamDownloadSupported() {
  return (
    typeof window !== "undefined" &&
    typeof document !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof MessageChannel === "function"
  );
}

async function workerFor() {
  if (!readyPromise) {
    readyPromise = (async () => {
      const reg = await navigator.serviceWorker.register(WORKER_URL, { scope: "/" });
      await navigator.serviceWorker.ready;
      // A page loaded before the worker existed is not controlled yet;
      // activate()'s clients.claim() flips that within a tick or two.
      if (!navigator.serviceWorker.controller) {
        await new Promise((resolve) => {
          const timer = setTimeout(resolve, 4000);
          navigator.serviceWorker.addEventListener(
            "controllerchange",
            () => {
              clearTimeout(timer);
              resolve();
            },
            { once: true },
          );
        });
      }
      return navigator.serviceWorker.controller || reg.active || null;
    })().catch((e) => {
      readyPromise = null;
      throw e;
    });
  }
  return readyPromise;
}

/**
 * Start a download the browser saves itself. Returns a writer whose write()
 * promises resolve only once the worker has taken the bytes, so the download
 * cannot outrun the disk or balloon the tab's memory.
 */
export async function openStreamDownload(filename, { contentType = "video/mp4", size = null } = {}) {
  if (!streamDownloadSupported()) throw new StreamDownloadUnavailable("Streaming downloads unavailable");
  let worker = null;
  try {
    worker = await workerFor();
  } catch (e) {
    throw new StreamDownloadUnavailable(`Download worker failed: ${e?.message || e}`);
  }
  if (!worker) throw new StreamDownloadUnavailable("Download worker not controlling this page");

  const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  const channel = new MessageChannel();
  const port = channel.port1;

  // Chunks wait here until the worker asks for one, so at most a single chunk
  // is ever buffered on this side: the worker pulls only what the browser is
  // ready to write to disk.
  const queue = [];
  let waitingPull = false;
  let closed = false;
  let aborted = false;
  let accepted = false;
  // Callbacks installed by openStreamDownload()/close(); both are one-shot.
  let handshake = null;
  let finished = null;

  const cleanup = () => {
    try {
      iframe.remove();
    } catch {
      /* already gone */
    }
  };

  const fail = (error) => {
    aborted = true;
    while (queue.length) queue.shift().reject(error);
  };

  // Answer the worker's outstanding request: the next chunk, the end marker,
  // or a cancellation. Called whenever either side changes that answer.
  const pump = () => {
    if (!waitingPull) return;
    const settle = (msg, hook) => {
      waitingPull = false;
      try {
        port.postMessage(msg);
      } catch {
        /* port gone */
      }
      hook?.();
    };
    if (aborted) return settle({ error: "Cancelled" });
    const next = queue.shift();
    if (next) {
      waitingPull = false;
      try {
        port.postMessage({ chunk: next.chunk });
        next.resolve();
      } catch (e) {
        next.reject(e);
      }
      return;
    }
    if (closed) return settle({ done: true }, () => finished?.());
    // Nothing to send yet — write() pumps again when a chunk arrives.
  };

  port.onmessage = (event) => {
    const msg = event.data || {};
    if (msg.type === "ready") {
      accepted = true;
      handshake?.();
      return;
    }
    if (msg.type === "pull") {
      waitingPull = true;
      pump();
      return;
    }
    if (msg.type === "cancel") {
      // The browser gave up on the download (cancelled from the download
      // shelf, or the response errored). Stop producing bytes.
      fail(abortError());
      cleanup();
    }
  };
  port.start();

  try {
    worker.postMessage({ type: "poku-download", id, filename, contentType, size }, [channel.port2]);
  } catch (e) {
    throw new StreamDownloadUnavailable(`Could not hand the download to the worker: ${e?.message || e}`);
  }

  const iframe = document.createElement("iframe");
  iframe.hidden = true;
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.display = "none";
  iframe.src = `${DOWNLOAD_PATH}?id=${encodeURIComponent(id)}`;
  document.body.appendChild(iframe);

  // The worker answers the iframe's request itself; if it never acknowledges
  // the postMessage, the request went somewhere else (a stale worker version,
  // or no controller) and streaming is not available.
  try {
    await new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new StreamDownloadUnavailable("The download worker did not take the job")),
        READY_TIMEOUT_MS,
      );
      handshake = () => {
        clearTimeout(timer);
        resolve();
      };
      if (accepted) handshake();
    });
  } catch (e) {
    cleanup();
    throw e;
  }

  return {
    write(chunk) {
      if (aborted) return Promise.reject(abortError());
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          const idx = queue.findIndex((q) => q.timer === timer);
          if (idx >= 0) queue.splice(idx, 1);
          const err = new StreamDownloadUnavailable("The browser did not accept the download");
          fail(err);
          cleanup();
          reject(err);
        }, PICKUP_TIMEOUT_MS);
        queue.push({
          chunk,
          timer,
          resolve: () => {
            clearTimeout(timer);
            resolve();
          },
          reject: (e) => {
            clearTimeout(timer);
            reject(e);
          },
        });
        pump();
      });
    },
    async close() {
      if (closed || aborted) return;
      closed = true;
      // Resolve once the worker has been told the stream ended, i.e. every
      // byte has been handed over.
      await new Promise((resolve) => {
        const timer = setTimeout(resolve, 5000);
        finished = () => {
          clearTimeout(timer);
          resolve();
        };
        pump();
      });
      // Give the browser a moment to commit the download before the iframe
      // (the client it rode in on) disappears.
      setTimeout(cleanup, 5000);
    },
    async abort() {
      if (closed || aborted) return;
      fail(abortError());
      pump();
      setTimeout(cleanup, 1000);
    },

  };
}
